import { useEffect, useState } from 'react';
import { IToolSearchResult } from 'types/quoteEditor.types';
import useDebounce from 'hooks/useDebounce';
import algoliaService from 'services/algolia.service';
import ToolSearchMetabaseRow from '../ToolSearchMetabaseRow/ToolSearchMetabaseRow';
import ToolSearchDefault from '../ToolSearchDefault/ToolSearchDefault';
import ToolSearchResults from '../ToolSearchResults/ToolSearchResults';
import ToolSearchEmpty from '../ToolSearchEmpty/ToolSearchEmpty';
import { bordColorTheme } from '../../../../../../../styles/styles.constants';
import { BordOneToneIcon } from 'bord-design-system';
import GenericLoader from 'components/GenericLoader';
import './QuoteToolSearch.scss';

interface IQuoteToolSearch {
  countryCode: string;
  onSelectTool: (tool: IToolSearchResult) => void;
  onCreateNew: () => void;
}

/**
 * Orquestador del flujo de búsqueda de herramientas por SKU o modelo.
 * Gestiona el estado de la búsqueda y delega el renderizado a sub-componentes
 * según si hay resultados, no hay resultados, o no hay búsqueda activa.
 */
const QuoteToolSearch = ({ countryCode, onSelectTool, onCreateNew }: IQuoteToolSearch) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<IToolSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedQuery = useDebounce(searchQuery, 500);
  const hasResults = !isSearching && searchResults.length > 0;
  const noResults = debouncedQuery.length > 0 && !isSearching && searchResults.length === 0;
  const showDefault = !debouncedQuery && !isSearching;

  /** Ejecuta la búsqueda en Algolia cuando el query debounceado cambia. */
  useEffect(() => {
    if (!debouncedQuery) {
      setSearchResults([]);
      return;
    }
    const { searchProducts } = algoliaService();
    setIsSearching(true);
    searchProducts(debouncedQuery, countryCode)
      .then(setSearchResults)
      .finally(() => setIsSearching(false));
  }, [debouncedQuery, countryCode]);

  return (
    <div className="quoteToolSearch">
      <p className="searchLabel">Busca las herramientas a cotizar</p>
      <div className={`searchInputWrapper${searchQuery ? ' hasValue' : ''}`}>
        <BordOneToneIcon variant="search" standardSize={14} stroke={bordColorTheme.navyBlue[400]} />
        <input
          placeholder="Busca por código de SKU o modelo"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>
      <ToolSearchMetabaseRow />
      <div className="searchDivider" />
      {isSearching && (
        <div className="searchingLoader">
          <GenericLoader sizeLoader={28} colorLoader={bordColorTheme.cyan[400]} />
        </div>
      )}
      {showDefault && <ToolSearchDefault onCreateNew={onCreateNew} />}
      {hasResults && (
        <ToolSearchResults results={searchResults} onSelectTool={onSelectTool} onCreateNew={onCreateNew} />
      )}
      {noResults && <ToolSearchEmpty onCreateNew={onCreateNew} />}
    </div>
  );
};

export default QuoteToolSearch;
